import { Link, useParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { ArrowLeft, ArrowUpRight, Check } from "lucide-react";
import { useLang } from "@/i18n/LangContext";
import { projects } from "@/data/projects";
import BrowserMockup from "@/components/BrowserMockup";
import Reveal from "@/components/Reveal";
import NotFound from "./NotFound";

export default function ProjectDetail() {
  const { slug } = useParams();
  const { t, link, lang } = useLang();
  const p = projects.find((x) => x.slug === slug);
  if (!p) return <NotFound />;

  return (
    <>
      <Helmet>
        <title>{`${p.name} | DIGITAL COMPANY`}</title>
        <meta name="description" content={p.desc[lang]} />
      </Helmet>
      <section className="bg-ink bg-grid-dark pt-[140px] pb-14 lg:pt-[180px]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal>
            <Link to={link("/projects")} data-testid="project-back" className="inline-flex items-center gap-2 text-sm text-steel hover:text-white transition-colors">
              <ArrowLeft className="h-4 w-4" /> {t("projectDetail.back")}
            </Link>
            <p className="mt-8 text-xs font-semibold tracking-[0.25em] text-electric">{p.category[lang]}</p>
            <h1 className="mt-4 font-display text-4xl sm:text-5xl font-extrabold tracking-tight">{p.name}</h1>
            <div className="mt-6 flex flex-wrap gap-2">
              {p.tags.map((tag) => (
                <span key={tag} className="rounded-full border border-white/15 px-3.5 py-1.5 text-xs text-steel">{tag}</span>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      <section className="bg-white text-ink py-16 lg:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal>
            <BrowserMockup img={p.img} url={p.url} name={p.name} />
          </Reveal>
          <div className="mt-14 grid gap-12 lg:grid-cols-3">
            <Reveal className="lg:col-span-2">
              <h2 className="text-[11px] font-semibold tracking-[0.25em] text-dblue">{t("projectDetail.overview")}</h2>
              <p className="mt-4 text-base sm:text-lg text-slate-700 leading-relaxed">{p.desc[lang]}</p>
              <h2 className="mt-10 text-[11px] font-semibold tracking-[0.25em] text-dblue">{t("projectDetail.services")}</h2>
              <ul className="mt-4 space-y-3">
                {p.services[lang].map((s) => (
                  <li key={s} className="flex items-start gap-2.5 text-sm text-slate-700">
                    <Check className="mt-0.5 h-4 w-4 shrink-0 text-dblue" /> {s}
                  </li>
                ))}
              </ul>
            </Reveal>
            <Reveal delay={0.1}>
              <div className="rounded-2xl border border-ink/10 bg-mist p-7">
                <h2 className="text-[11px] font-semibold tracking-[0.25em] text-dblue">{t("projectDetail.website")}</h2>
                <a
                  href={p.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="project-visit"
                  className="mt-4 inline-flex items-center gap-2 rounded-full bg-ink px-6 py-3 text-sm font-semibold text-white transition-colors hover:bg-dblue"
                >
                  {t("projectDetail.visit")} <ArrowUpRight className="h-4 w-4" />
                </a>
                <p className="mt-3 font-mono text-xs text-slate-500">{p.url}</p>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="bg-ink2 bg-grid-dark py-16 lg:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Reveal>
            <h2 className="font-display text-2xl sm:text-3xl font-bold tracking-tight">{t("projectDetail.cta")}</h2>
            <Link
              to={link("/contact")}
              data-testid="project-contact-cta"
              className="mt-7 inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-dblue to-electric px-7 py-3.5 text-sm font-semibold transition-transform duration-200 hover:scale-[1.04]"
            >
              {t("projectDetail.ctaBtn")} <ArrowUpRight className="h-4 w-4" />
            </Link>
          </Reveal>
        </div>
      </section>
    </>
  );
}
